// src/dashboard/widgets/ThreadsRailWidget.tsx
import { MessageSquare } from "lucide-react";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { useSupervisionThreads } from "@/shared/hooks/useSupervisionThreads";
import { getDiceAvatarUrl } from "@/shared/lib/utils";
import { registerWidget } from "@/shared/dashboard/widgets/registry";
import type { WidgetProps } from "@/shared/dashboard/widgets/types";

type L = "en" | "uk" | "pl" | "ro";

const T = {
  conversations: {
    en: "Conversations",
    uk: "Розмови",
    pl: "Rozmowy",
    ro: "Conversații",
  },
  all: { en: "All", uk: "Усі", pl: "Wszystkie", ro: "Toate" },
  loading: {
    en: "Loading…",
    uk: "Завантаження…",
    pl: "Ładowanie…",
    ro: "Se încarcă…",
  },
  noThreads: {
    en: "No conversations yet.",
    uk: "Розмов ще немає.",
    pl: "Brak rozmów.",
    ro: "Nicio conversație.",
  },
};

const t = (locale: L, map: Record<L, string>) => map[locale] ?? map.en;

function formatRelative(iso: string): string {
  const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d`;
  return new Date(iso).toLocaleDateString();
}

function ThreadsRailWidgetImpl(_: WidgetProps) {
  const { locale } = useLanguage();
  const { threads, loading } = useSupervisionThreads();
  const l = locale as L;

  // Thread click → navigate to /supervisor, which reads activeThreadId from
  // sessionStorage on mount and restores the thread inline. Handshake fixed
  // in commit f2d74e5.
  const onSelectThread = (id: string) => {
    sessionStorage.setItem("activeThreadId", id);
    (window as any).navigate?.("/supervisor");
  };

  const onViewAll = () => (window as any).navigate?.("/supervisor");

  return (
    <div className="flex flex-col h-full min-h-0">
      <header className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
        <p className="ed-mono text-[10px] text-[var(--muted)] flex items-baseline gap-2">
          {t(l, T.conversations)}
          {threads.length > 0 && (
            <span className="text-[var(--faint)] tabular-nums">
              {threads.length}
            </span>
          )}
        </p>
        <button
          onClick={onViewAll}
          className="ed-mono text-[10px] hover:underline transition cursor-pointer"
          style={{ color: "var(--accent)" }}
        >
          {t(l, T.all)} →
        </button>
      </header>

      <div className="flex-1 overflow-y-auto min-h-0 px-3 pb-3 space-y-0.5">
        {loading && threads.length === 0 ? (
          <div className="px-2 py-2 space-y-2 animate-pulse">
            <div className="h-9 rounded-[4px] bg-[var(--paper-2)]" />
            <div className="h-9 rounded-[4px] bg-[var(--paper-2)]" />
          </div>
        ) : threads.length === 0 ? (
          <div className="px-2.5 py-3 text-sm text-[var(--muted)]">
            {t(l, T.noThreads)}
          </div>
        ) : (
          threads.map((thread) => {
            const isPatient = thread.type === "patient" && thread.patient_name;
            const hasDescriptor = !!thread.patient_descriptor;
            const showAvatar = isPatient || hasDescriptor;
            const avatarSeed =
              thread.patient_name || thread.patient_descriptor || thread.id;
            const displayName = isPatient
              ? thread.patient_name!
              : hasDescriptor
                ? thread.patient_descriptor!
                : (thread.title ?? "Untitled");
            // Collapse the up-to-three sub-lines to a single, deduped secondary.
            const secondary =
              hasDescriptor && displayName !== thread.patient_descriptor
                ? thread.patient_descriptor
                : thread.title && displayName !== thread.title
                  ? thread.title
                  : !showAvatar
                    ? (thread.preview ?? null)
                    : null;
            return (
              <button
                key={thread.id}
                onClick={() => onSelectThread(thread.id)}
                className="w-full text-left rounded-[4px] px-2.5 py-2.5 hover:bg-[rgba(12,26,51,0.03)] transition cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  {showAvatar ? (
                    <img
                      src={getDiceAvatarUrl(avatarSeed)}
                      alt={displayName}
                      className="w-9 h-9 rounded-full bg-[var(--paper-2)] ring-1 ring-[var(--hairline)] flex-shrink-0"
                    />
                  ) : (
                    <div
                      className="w-9 h-9 rounded-full ring-1 ring-[var(--hairline)] flex items-center justify-center flex-shrink-0"
                      style={{
                        background:
                          "color-mix(in srgb, var(--accent) 10%, white)",
                      }}
                    >
                      <MessageSquare
                        size={15}
                        style={{ color: "var(--accent)" }}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-[var(--ink)] truncate">
                        {displayName}
                      </span>
                      <span className="text-[11px] text-[var(--faint)] tabular-nums flex-shrink-0">
                        {formatRelative(thread.last_message_at)}
                      </span>
                    </div>
                    {secondary && (
                      <p className="text-xs text-[var(--muted)] truncate mt-0.5">
                        {secondary}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

registerWidget({
  id: "threadsRail",
  component: ThreadsRailWidgetImpl,
  supportedSlots: new Set(["sideRail"]),
  displayName: "Supervision Threads",
});

export const ThreadsRailWidget = ThreadsRailWidgetImpl;
